import { SET, SET_ITEM } from '../';
import { SearchService } from '@/services';
import { GeoCode } from '@/services/models';

export default {
	namespaced: true,
	state: {
		locations: [],
		markers: [],
		details: [],
		availability: [],
		mapIsFullscreen: false
	},
	mutations: {
		SET,
		SET_ITEM
	},
	actions: {
		async getLocationFromAddress({ commit, state }, address) {
			// Get from vuex store
			let location = state.locations.find(l => l.address == address);
			if (location) return location.location;

			// Else, perform new search
			location = await SearchService.GetLocationFromAddress(address);
			if (location) {
				commit('SET_ITEM', { key: 'locations', find: l => l.address.toLowerCase() == address.toLowerCase(), value: new GeoCode({ address, location }) });

				// Also add formatted_address as key in store.
				if (location.formatted_address.toLowerCase() != address.toLowerCase()) {
					commit('SET_ITEM', { key: 'locations', find: l => l.address.toLowerCase() == location.formatted_address.toLowerCase(), value: new GeoCode({ location }) });
				}
			}

			return location;
		},
		async getLocations({ commit, state },search){
			let locations = await SearchService.GetLocations(search);
			return locations;
		},
		async getLocationFromLatLng({ commit, state }, { latitude, longitude }) {
			// Get from vuex store
			let location = state.locations.find(l => l.latitude == latitude && l.longitude == longitude);
			if (location) return location.location;

			// Else, perform new search
			location = await SearchService.GetLocationFromLatLng(latitude, longitude);
			if (location) {
				commit('SET_ITEM', { key: 'locations', find: l => l.latitude == latitude && l.longitude == longitude, value: new GeoCode({ latitude, longitude, location }) });
			}

			return location;
		},

		/**
		 * Get markers for sites.
		 * @param {Number[]=} siteIds - Array of siteIds to filter the markers for. If empty or undefined, return all sites.
		 */
		async getMarkersForSites({ commit, state }, siteIds = []) {
			// 1. Get markers from vuex store
			let markers = siteIds.length ? state.markers.filter(m => siteIds.includes(m.siteId)) : state.markers;

			// 2. Fetch markers remaining from server
			if (!markers.length || siteIds.length > markers.length) { 
				const markersFromServer = await SearchService.GetLocationsForSites(siteIds.filter(siteId => !markers.some(d => d.siteId == siteId)));
				markers = markers.concat(markersFromServer);

				// Update vuex store
				for (const mfs of markersFromServer) {
					commit('SET_ITEM', { key: 'markers', find: m => m.siteId == mfs.siteId, value: mfs });
				}
			}

			return markers;
		},

		/**
		 * Get detailed information for sites.
		 * @param {Number[]=} siteIds - Array of siteIds to filter details for. If empty or undefined, returns for all sites.
		 */
		async getDetailsForSites({ commit, state }, siteIds = []) {
			// 1. Get details from vuex store
			let details = siteIds.length ? state.details.filter(d => siteIds.includes(d.siteId)) : state.details;

			// 2. Fetch details remaining from server
			if (!details.length || siteIds.length > details.length) {
				const detailsFromServer = await SearchService.GetDetailsForSites(siteIds.filter(siteId => !details.some(d => d.siteId == siteId)))
				details = details.concat(detailsFromServer);

				// Update vuex store
				for (const dfs of detailsFromServer) {
					commit('SET_ITEM', { key: 'details', find: d => d.siteId == dfs.siteId, value: dfs });
				}
			}

			return details;
		},

		/**
		 * Get available time for sites and selected date and treatment/category.
		 * @param {Number[]=} siteIds - Array of siteIds to filter details for. If empty or undefined, returns for all sites.
		 * @param {Date=} date - The date to get available time for.
		 * @param {Number=} treatmentId - The treatment to get available time for.  
		 */
		async getAvailableTimeForSites({ commit, state }, { siteIds = [], date = new Date(), treatmentId = 0 }) {
			date.setHours(0, 0, 0, 0);

			// 1. Get availability from vuex store
			let availability = siteIds.length ? state.availability.reduce((availableTimeList, site) => {
				const availableTime = siteIds.includes(site.siteId) && site.availableTime.find(a => a.date.getTime() == date.getTime() && a.treatmentId == treatmentId);
				if (availableTime) {
					availableTimeList.push(availableTime);
				}

				return availableTimeList;
			}, []) : [];

			// 2. Fetch available time for remaining sites from server
			if (!availability.length || siteIds.length > availability.length) {
				const availabilityFromServer = await SearchService.GetAvailableTimeBySalons(siteIds.filter(siteId => !availability.some(d => d.siteId == siteId)), date, treatmentId);
				availability = availability.concat(availabilityFromServer);

				// Update vuex store
				for (const afs of availabilityFromServer) {
					commit('SET_ITEM', { key: 'availability', find: a => a.siteId == afs.siteId, value: item => ({
						siteId: afs.siteId,
						availableTime: (item ? item.availableTime : []).concat(afs)
					})});
				}
			}
			
			return availability;
		},

		toggleMapIsFullscreen({ commit, state }) {
			commit('SET', { key: 'mapIsFullscreen',value: !state.mapIsFullscreen });
		}
	},
	getters: {
		details: state => state.details.slice(),
		detailsBySiteId: state => siteId => state.details.find(d => d.siteId == siteId),
		mapIsFullscreen: state => state.mapIsFullscreen,
		availableSites: state => (date, treatmentId /* , categoryId */) => {
			if (date) {
				date.setHours(0, 0, 0, 0);

				return state.availability.reduce((siteIds, site) => {
					if (site.availableTime.some(a => a.available && a.date.getTime() == date.getTime() && a.treatmentId == treatmentId)) {
						siteIds.push(site.siteId);
					}
		
					return siteIds;
				}, []);
			}

			return state.availability.map(a => a.siteId);
		}
	},
	
};
